import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Eye, EyeOff, Copy, Lock, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PORTALS = ["GST", "Income Tax", "Professional Tax", "TDS TRACES", "TDS Income Tax", "Other"];
const portalColors = {
  "GST": "border-blue-500",
  "Income Tax": "border-violet-500",
  "Professional Tax": "border-amber-500",
  "TDS TRACES": "border-emerald-500",
  "TDS Income Tax": "border-teal-500",
  "Other": "border-slate-400",
};

export default function ClientCredentials({ clientId }) {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [reveal, setReveal] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ portal: "GST", label: "", username: "", password: "", url: "", remarks: "" });
  const canManage = me?.role === "Owner" || me?.role === "Partner";

  async function load() {
    try {
      const { data } = await api.get(`/clients/${clientId}/credentials`);
      setRows(data);
    } catch (err) { console.error("Failed to load credentials", err); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post(`/clients/${clientId}/credentials`, form);
      toast.success("Credential saved (encrypted)");
      setOpen(false);
      setForm({ portal: "GST", label: "", username: "", password: "", url: "", remarks: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  async function doReveal(idx) {
    if (reveal[idx]) {
      setReveal(r => { const n = { ...r }; delete n[idx]; return n; });
      return;
    }
    try {
      const { data } = await api.get(`/clients/${clientId}/credentials/${idx}/reveal`);
      setReveal(r => ({ ...r, [idx]: data.password }));
      setTimeout(() => setReveal(r => { const n = { ...r }; delete n[idx]; return n; }), 30000);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  async function del(idx) {
    if (!confirm("Delete this credential?")) return;
    await api.delete(`/clients/${clientId}/credentials/${idx}`);
    load();
  }

  if (me?.role === "Employee") {
    return (
      <Card className="p-8 text-center">
        <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
        <div className="text-sm text-muted-foreground">Credentials are visible only to Owner &amp; Partner.</div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-credentials-tab">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Lock className="w-3 h-3" /> AES-256 encrypted at rest
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-cred-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-3.5 h-3.5 mr-1.5" />Add credential</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New portal credential</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div><Label>Portal *</Label>
                  <Select value={form.portal} onValueChange={v => setForm({ ...form, portal: v })}>
                    <SelectTrigger data-testid="cred-portal-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PORTALS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.portal === "Other" && (
                  <div><Label>Label</Label><Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. MCA, ICEGATE, PF, ESIC" /></div>
                )}
                <div><Label>URL</Label><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} className="font-mono" placeholder="https://gst.gov.in" /></div>
                <div><Label>Username</Label><Input data-testid="cred-username-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className="font-mono" /></div>
                <div><Label>Password</Label><Input data-testid="cred-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="font-mono" /></div>
                <div><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} /></div>
                <DialogFooter><Button data-testid="save-cred-btn" type="submit" className="bg-blue-600 hover:bg-blue-700">Save (encrypted)</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {rows.length === 0 && (
        <Card className="p-8 text-center">
          <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="text-sm text-muted-foreground">No portal credentials yet. Add GST / IT / TDS / PT logins for this client.</div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(cr => {
          const revealed = reveal[cr.idx];
          return (
            <Card key={cr.idx} className={`p-4 border-l-[3px] ${portalColors[cr.portal] || "border-slate-400"}`} data-testid={`cred-card-${cr.idx}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{cr.portal}{cr.label ? ` · ${cr.label}` : ""}</div>
                  <div className="text-sm font-mono mt-1">{cr.username || "—"}</div>
                </div>
                {cr.url && (
                  <a href={cr.url.startsWith("http") ? cr.url : `https://${cr.url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="w-16 text-muted-foreground uppercase tracking-widest text-[10px]">Pass</span>
                <span className="font-mono flex-1 truncate">{revealed || cr.password_mask || "—"}</span>
                <button data-testid={`reveal-cred-${cr.idx}`} onClick={() => doReveal(cr.idx)} className="text-muted-foreground hover:text-blue-600">
                  {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                {revealed && (
                  <button onClick={() => { navigator.clipboard.writeText(revealed); toast.success("Copied"); }} className="text-muted-foreground hover:text-blue-600">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {cr.remarks && <div className="mt-2 text-[10px] text-muted-foreground italic">{cr.remarks}</div>}
              {canManage && (
                <div className="mt-2 pt-2 border-t border-border flex justify-end">
                  <button data-testid={`delete-cred-${cr.idx}`} onClick={() => del(cr.idx)} className="text-[10px] text-red-600 hover:underline inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
